/**
 * App 3 derivations (spec §11). Pure, no I/O, no configuration baked in.
 *
 * Everything here takes the organisation's configured rules as an argument
 * rather than knowing them. Spec §11 prints a priority matrix and a set of
 * SLAs, and it would be an easy afternoon's work to type that table into this
 * file — which is exactly what Rule 1 forbids. The matrix is one Algihaz will
 * revise; a revision must be an Org Admin editing escalation rules, not a
 * deploy.
 *
 * So what lives here is the *shape* of the decisions and the edge cases that
 * are easy to get quietly wrong, and nothing that belongs in a database row.
 */
/**
 * The vocabulary comes from `domain/status.ts`, where Phase 1 put it in
 * anticipation of this phase. It is imported rather than restated: two
 * definitions of the same enum are two things that can disagree, and the one
 * that disagrees is always the one a screen happens to import.
 */
/**
 * Whether a finding is a non-conformity at all.
 *
 * A conformity is a *good* result and an observation is a note, so neither
 * spawns a corrective action or an SLA clock. Conflating "has no priority" with
 * "is not urgent" would put every conformity on somebody's overdue list.
 */
export function isNonConformity(classification) {
    return classification === 'minor_nc' || classification === 'major_nc';
}
/**
 * Whether a finding still needs work (DATA-9; decided with the owner
 * 2026-09-26).
 *
 * Only a non-conformity is ever open. An observation or a conformity is a
 * record, not an action: it needs no corrective action, so it counts as closed
 * from the moment it is raised and never blocks its audit. Derived from the
 * classification rather than stamped on the row, so findings raised before this
 * rule read correctly without any data being rewritten (ADR-0018).
 */
export function isFindingOpen(finding) {
    if (finding.deletedAt != null)
        return false;
    if (finding.classification && !isNonConformity(finding.classification))
        return false;
    return finding.closedAt == null;
}
/**
 * Resolve a finding's priority from the configured matrix.
 *
 * Returns `null` rather than a default when nothing matches, and the caller must
 * decide what that means. A silent fallback to P6 would be the worst possible
 * answer: an unmatched combination is a *configuration gap*, and dressing it up
 * as "lowest priority" hides the gap behind a plausible number — the same class
 * of defect as an escalation level that notified nobody while appearing to work.
 */
export function resolvePriority(rules, finding) {
    if (!isNonConformity(finding.classification))
        return null;
    if (!finding.riskLevel)
        return null;
    const wanted = finding.riskLevel.trim().toLowerCase();
    const match = rules.find((rule) => rule.classification === finding.classification &&
        rule.riskLevel.trim().toLowerCase() === wanted);
    return match?.priority ?? null;
}
/**
 * How far through its checklist an audit is, and what it has left dangling.
 *
 * `total` is the number of items on the template, supplied separately, because
 * an item with no response row at all is **unanswered** rather than absent.
 * Counting only the rows that exist would report an audit that has answered
 * three of forty questions as 100% complete, which is the most flattering
 * possible way to be wrong.
 */
export function checklistProgress(templateItemIds, answers) {
    const live = answers.filter((answer) => answer.deletedAt == null);
    const byItem = new Map(live.map((answer) => [answer.templateItemId, answer]));
    let answered = 0;
    let nonConforming = 0;
    let nonConformingWithoutFinding = 0;
    for (const itemId of templateItemIds) {
        const answer = byItem.get(itemId);
        if (!answer || answer.response === 'unanswered')
            continue;
        answered += 1;
        if (answer.response !== 'does_not_conform')
            continue;
        nonConforming += 1;
        if (!answer.findingId)
            nonConformingWithoutFinding += 1;
    }
    return {
        total: templateItemIds.length,
        answered,
        unanswered: templateItemIds.length - answered,
        nonConforming,
        nonConformingWithoutFinding,
    };
}
/**
 * Why this audit cannot be closed yet, in the reader's own terms.
 *
 * Returns a list rather than a boolean for the same reason App 2's submit
 * refusal does: "this audit cannot be closed" leaves a lead auditor guessing
 * which of forty checklist items or which of six findings is the problem. An
 * empty list means it can close.
 *
 * `not_applicable` counts as answered — deciding a question does not apply to
 * this audit IS an answer, and the most common one in a scoped audit.
 */
export function auditClosureBlockers(input) {
    const blockers = [];
    if (!input.closingMeetingAt) {
        blockers.push('The closing meeting has not been recorded.');
    }
    if (input.progress.unanswered > 0) {
        blockers.push(`${input.progress.unanswered} checklist item(s) have not been answered. Mark anything out of scope as not applicable.`);
    }
    if (input.progress.nonConformingWithoutFinding > 0) {
        // The gap the response table exists to expose.
        blockers.push(`${input.progress.nonConformingWithoutFinding} checklist item(s) were marked non-conforming but no finding was raised.`);
    }
    const openFindings = input.findings.filter(isFindingOpen).length;
    if (openFindings > 0) {
        blockers.push(`${openFindings} non-conformit${openFindings === 1 ? 'y is' : 'ies are'} still open. One closes when each responsible department's latest corrective action is verified effective.`);
    }
    return blockers;
}
/**
 * The CAR statuses that count as finished and effective.
 *
 * `closed_not_effective` is deliberately absent: a corrective action that did
 * not work is a reason to schedule a follow-up, not a reason to close the
 * finding it came from.
 */
export const EFFECTIVE_CAR_STATUSES = ['closed_effective'];
/** The CAR status that ends a CAR without its action having worked. */
export const INEFFECTIVE_CAR_STATUS = 'closed_not_effective';
/**
 * ADR-0012: a finding closes when every responsible department's **current**
 * corrective action is verified effective.
 *
 * A department's current CAR is its one that is not `closed_not_effective`: an
 * ineffective CAR is finished but did not work, and is replaced by a follow-up
 * CAR to the same department (DATA-8; decided with the owner 2026-09-26). So:
 *
 *  - Some department's current CAR still open → the finding stays open.
 *  - **A department whose only CARs were ineffective → open**: it needs a
 *    follow-up. This used to be a dead end, because no second CAR could be
 *    issued and an ineffective one could never become effective.
 *  - **No CARs at all → the finding does NOT close.** `Array.every` on an empty
 *    array returns `true`, so the obvious implementation closes every finding
 *    the moment it is raised. A non-conformity with no corrective action is an
 *    omission, not an achievement.
 *  - Every department's current CAR effective → closed.
 *
 * Soft-deleted CARs are excluded first (ADR-0006). A CAR with no department
 * given is its own department, which is how older callers counted.
 */
export function isFindingClosed(cars) {
    const live = cars.filter((car) => car.deletedAt == null);
    if (live.length === 0)
        return false;
    const byDepartment = new Map();
    live.forEach((car, index) => {
        const key = car.department ?? `car-${index}`;
        byDepartment.set(key, [...(byDepartment.get(key) ?? []), car]);
    });
    return [...byDepartment.values()].every((group) => {
        const current = group.find((car) => car.status !== INEFFECTIVE_CAR_STATUS);
        return Boolean(current && EFFECTIVE_CAR_STATUSES.includes(current.status));
    });
}
/**
 * The date an escalation clock should actually measure against.
 *
 * Spec §11: a Process/Product Owner may request an extension, which the Audit
 * Lead approves, and *"the escalation clock only re-applies once the approved
 * extension elapses"*. Two things follow, and both have been got wrong before:
 *
 *  - A **requested** extension changes nothing. Only an approved one moves the
 *    date, or the owner sets their own deadline by asking.
 *  - An approved extension that is **earlier** than the original target does not
 *    pull the deadline in. An extension extends; that is what the word means.
 */
export function effectiveDueDate(car) {
    const { targetCompletionDate, extensionRequestedUntil, extensionApprovedAt } = car;
    if (!extensionApprovedAt || !extensionRequestedUntil)
        return targetCompletionDate;
    if (!targetCompletionDate)
        return extensionRequestedUntil;
    return extensionRequestedUntil > targetCompletionDate
        ? extensionRequestedUntil
        : targetCompletionDate;
}
export function countOpenWork(findings, cars) {
    const liveFindings = findings.filter((finding) => finding.deletedAt == null);
    const liveCars = cars.filter((car) => car.deletedAt == null);
    return {
        findings: liveFindings.length,
        findingsOpen: liveFindings.filter(isFindingOpen).length,
        cars: liveCars.length,
        // An ineffective CAR is finished (its follow-up is the open one), so only
        // CARs still being worked or verified count as open.
        carsOpen: liveCars.filter((car) => !EFFECTIVE_CAR_STATUSES.includes(car.status) && car.status !== INEFFECTIVE_CAR_STATUS).length,
    };
}
//# sourceMappingURL=audit.js.map